import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CancelOutlined from "@mui/icons-material/CancelOutlined";
import AnalyticsWidgetHeader from "./AnalyticsWidgetHeader";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
} from "./dashboardWidgetLayout";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoginResponse } from "subModule/src/services/userProfile/interface";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import { GetStationExpenseTypeResponse } from "subModule/src/services/analytics/interface";
import { useGetServiceCancellationReasonsService } from "subModule/src/services/analytics/useAnalytics";
import { InfiniteData } from "@tanstack/react-query";
import { GetStationDropdownResponse } from "subModule/src/services/station/interface";

interface ServiceCancellationReasonsProps {
  user: LoginResponse | undefined;
  themes: any;
  stations: { id: number; name: string }[] | [];
  enqueueSnackbar: EnqueueSnackbar;
  stationsLoaded: boolean;
  handleDropdownActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  setPageNo: any;
  stationsData: InfiniteData<GetStationDropdownResponse, unknown> | undefined;
  searchLoading: string | null;
  pageNo: number;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  globalFilters: { stationId: any; range: any; allStation: boolean };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
}

export default function ServiceCancellationReasons(
  props: ServiceCancellationReasonsProps,
) {
  const { t, i18n } = useTranslation();
  const firstRender = useRef(true);
  const screenWidth = useMediaQuery(
    "(min-width: 1200px) and (max-width: 1400px",
  );
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<{ [key: string]: any }>({
    defaultValues: {
      range: { value: "monthly", label: t("mtd") },
      allStation: false,
    },
  });
  const filters = useMemo(
    () => [
      { value: "daily", label: t("today") },
      { value: "yesterday", label: t("yesterday") },
      { value: "weekly", label: t("wtd") },
      { value: "monthly", label: t("mtd") },
      { value: "last_month", label: t("last_month") },
      { value: "yearly", label: t("ytd") },
    ],
    [i18n.language],
  );
  const values = watch(["stationId", "range", "allStation"]);
  const { refetch: apiRefetch } = useGetServiceCancellationReasonsService({
    stationId: values[0]?.value || null,
    range: values[1]?.value,
    allStation: values[2],
  });
  useEffect(() => {
    if (firstRender.current && props.stationsLoaded === true) {
      firstRender.current = false;
      setValue(
        "stationId",
        (props?.stations?.length > 1
          ? props?.stations?.[1]
          : props?.stations?.[0]) || null,
      );
      setTimeout(() => fetchData(), 50);
    }
  }, [props.stations]);
  useEffect(() => {
    const currentRange = watch("range");
    const updated = filters.find((f) => f.value === currentRange?.value);
    if (updated) {
      setValue("range", updated);
    }
  }, [i18n.language, filters]);
  useEffect(() => {
    if (props.globalFilterApplied === true) {
      setValue("stationId", props.globalFilters.stationId);
      setValue("range", props.globalFilters.range);
      setValue("allStation", props.globalFilters.allStation);
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);
  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: GetStationExpenseTypeResponse | unknown | any) => {
        if (res?.data?.success) {
          setData(res?.data?.data || {});
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied === true)
          props.setGlobalFilterApplied(false);
      });
  };

  const formattedData = Object.entries(data || {})
    ?.filter(([key, value]) => key !== "total" && Number(value) > 0)
    ?.map(([key, value]) => ({
      category: key,
      value: Number(value),
    }))
    ?.sort((a: any, b: any) => b.value - a.value);

  const maxValue = Math.max(
    ...(formattedData?.map((d: any) => Number(d.value)) || [1]),
    1,
  );

  const barStart = props.themes?.secondary || "#F59E0B";
  const barEnd = props.themes?.primary || "#EF4444";

  return (
    <Card
      sx={{
        height: "270px",
        marginTop: "16px",
        boxShadow: "none",
        borderRadius: "12px",
        border: "1px solid #F0F2F5",
        "& .MuiCardContent-root": {
          padding: "16px !important",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <CardContent>
        <Box sx={dashboardWidgetHeaderSx}>
          <AnalyticsWidgetHeader
            title={t("service") + " " + t("cancellationReasons")}
            icon={<CancelOutlined />}
            iconBg={`${props.themes?.primary || "#4F46E5"}18`}
            iconColor={props.themes?.primary || "#4F46E5"}
          />
          <Box sx={dashboardWidgetFiltersSx}>
            <Controller
              name="stationId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      minHeight: "auto",
                      fontSize: '9px',
                      height: "auto",
                      overflow: "hidden",
                      "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "15px",
                          height: "15px",
                        },
                      "& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator":
                        { position: "absolute", right: 0 },
                      border: 0,
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={props?.stations || []}
                    isDisabled={loading}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      if (selectedOption?.value === 0)
                        setValue("allStation", true);
                      else setValue("allStation", false);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.stationId?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    defaultPlaceholder={t("station")}
                    isLoading={props.searchLoading === "cancellationReasons"}
                    onInputChange={(
                      search: string,
                      { action }: { action: string },
                    ) => {
                      if (
                        action === "input-change" ||
                        (action !== "set-value" &&
                          action !== "menu-close" &&
                          action !== "input-blur")
                      )
                        props.handleDropdownActions(
                          "cancellationReasons",
                          "searchIt",
                          search,
                        );
                      else if (
                        action === "input-blur" ||
                        action === "menu-close"
                      ) {
                        if (props.dropdownSearchTerm || props.pageNo > 1)
                          props.handleDropdownActions(
                            "cancellationReasons",
                            "searchIt",
                            "",
                          );
                        else props.setDropdownSearchTerm("");
                        props.setPageNo(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(props.stationsData?.pages[0]?.data as any).last)
                        props.handleDropdownActions(
                          "cancellationReasons",
                          "loadNext",
                        );
                    }}
                    page={props.pageNo}
                  />
                </Box>
              )}
            />
            <Controller
              name="range"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      width: screenWidth ? "60px" : "auto",
                      minHeight: "auto",
                      fontSize: '9px',
                      height: "auto",
                      overflow: "hidden",
                      "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "15px",
                          height: "15px",
                        },
                      border: 0,
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={filters}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.range?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    isDisabled={loading}
                  />
                </Box>
              )}
            />
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ mt: 1.5, flex: 1 }}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton
                key={i}
                animation="pulse"
                height={42}
                sx={{
                  backgroundColor: props?.themes?.secondary,
                  mb: 1,
                  borderRadius: "8px",
                }}
              />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              mt: 1,
            }}
          >
            <Box className="flex items-center gap-1 mb-1.5">
              <Typography
                className="font-Saira"
                sx={{ fontSize: 12, fontWeight: 500, color: "#637381" }}
              >
                {t("total") + ":"}
              </Typography>
              <Typography
                className="font-Saira"
                sx={{
                  fontSize: 14,
                  fontWeight: 800,
                  color: props?.themes.primary,
                }}
              >
                {new Intl.NumberFormat("en", {
                  notation: "compact",
                  compactDisplay: "short",
                  maximumFractionDigits: 2,
                }).format(data?.total || 0)}
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 1,
                pr: 0.5,
                minHeight: 0,
              }}
            >
              {formattedData?.length === 0 || !data ? (
                <Box className="flex justify-center flex-col flex-1">
                  <Typography
                    className="font-Saira text-center italic font-semibold"
                    sx={{ color: props.themes.primary }}
                  >
                    {t("noRecordFound")}
                  </Typography>
                </Box>
              ) : (
                formattedData?.map((item: any) => {
                  const pct = Math.min(
                    100,
                    (Number(item.value) / maxValue) * 100,
                  );
                  return (
                    <Box
                      key={item.category}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        px: 1.25,
                        py: 1,
                        border: "1px solid #E5E7EB",
                        borderRadius: "10px",
                        backgroundColor: "#fff",
                        minWidth: 0,
                      }}
                    >
                      <Tooltip followCursor title={t(item.category)}>
                        <Typography
                          className="font-Saira"
                          sx={{
                            flex: "1 1 40%",
                            minWidth: 0,
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#1F2937",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            textAlign: "left",
                          }}
                        >
                          {t(item.category)}
                        </Typography>
                      </Tooltip>

                      <Box
                        sx={{
                          flex: "1 1 35%",
                          minWidth: 56,
                          height: 8,
                          borderRadius: 999,
                          backgroundColor: "#F3F4F6",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${pct}%`,
                            height: "100%",
                            borderRadius: 999,
                            background: `linear-gradient(90deg, ${barStart}, ${barEnd})`,
                            transition: "width 0.35s ease",
                          }}
                        />
                      </Box>

                      <Tooltip followCursor title={item?.value || 0}>
                        <Typography
                          className="font-Saira"
                          sx={{
                            flex: "0 0 auto",
                            fontSize: 12,
                            fontWeight: 800,
                            color: barEnd,
                            minWidth: 28,
                            textAlign: "right",
                          }}
                        >
                          {new Intl.NumberFormat("en", {
                            notation: "compact",
                            compactDisplay: "short",
                            maximumFractionDigits: 1,
                          }).format(item?.value || 0)}
                        </Typography>
                      </Tooltip>
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
